/**
 * Auto-Resize Service
 * Automatically resize iframe based on widget content using ResizeObserver
 */

import { MessageType } from '@/types';
import { getMessagingService } from './messaging';

/**
 * Resize service configuration
 */
interface ResizeConfig {
  /** Root element to observe */
  rootElement?: HTMLElement;

  /** Debounce delay in milliseconds */
  debounceDelay?: number;

  /** Minimum height */
  minHeight?: number;

  /** Maximum height */
  maxHeight?: number;

  /** Enable debug logging */
  debug?: boolean;

  /** Callback when resize occurs */
  onResize?: (height: number) => void;
}

/**
 * Auto-resize service using ResizeObserver
 */
export class ResizeService {
  private rootElement: HTMLElement;
  private observer: ResizeObserver | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private debounceDelay: number;
  private minHeight: number;
  private maxHeight: number;
  private debug: boolean;
  private onResize?: (height: number) => void;
  private lastHeight: number = 0;

  constructor(config: ResizeConfig = {}) {
    this.rootElement = config.rootElement || document.body;
    this.debounceDelay = config.debounceDelay || 100;
    this.minHeight = config.minHeight || 400;
    this.maxHeight = config.maxHeight || 10000;
    this.debug = config.debug || false;
    this.onResize = config.onResize;

    this.log('ResizeService initialized', {
      debounceDelay: this.debounceDelay,
      minHeight: this.minHeight,
      maxHeight: this.maxHeight,
    });
  }

  /**
   * Start observing resize events
   */
  public start(): void {
    if (this.observer) {
      this.log('ResizeObserver already running');
      return;
    }

    // Create ResizeObserver
    this.observer = new ResizeObserver((entries) => {
      this.handleResize(entries);
    });

    // Observe root element
    this.observer.observe(this.rootElement);

    // Send initial height
    this.sendInitialHeight();

    this.log('ResizeObserver started');
  }

  /**
   * Stop observing resize events
   */
  public stop(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
      this.log('ResizeObserver stopped');
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  /**
   * Handle resize events with debouncing
   */
  private handleResize(entries: ResizeObserverEntry[]): void {
    // Clear existing debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Debounce resize notifications
    this.debounceTimer = setTimeout(() => {
      for (const entry of entries) {
        const height = this.calculateHeight(entry);
        this.notifyHeightChange(height);
      }
    }, this.debounceDelay);
  }

  /**
   * Calculate height from ResizeObserver entry
   */
  private calculateHeight(entry: ResizeObserverEntry): number {
    // Get content height
    let height: number;

    if (entry.contentBoxSize) {
      // Use contentBoxSize if available (more accurate)
      const contentBoxSize = Array.isArray(entry.contentBoxSize)
        ? entry.contentBoxSize[0]
        : entry.contentBoxSize;
      height = Math.ceil(contentBoxSize.blockSize);
    } else {
      // Fallback to contentRect
      height = Math.ceil(entry.contentRect.height);
    }

    // Apply min/max constraints
    height = Math.max(this.minHeight, Math.min(height, this.maxHeight));

    return height;
  }

  /**
   * Send initial height on start
   */
  private sendInitialHeight(): void {
    const height = this.getCurrentHeight();
    this.notifyHeightChange(height);
  }

  /**
   * Get current height of root element
   */
  private getCurrentHeight(): number {
    const rect = this.rootElement.getBoundingClientRect();
    let height = Math.ceil(rect.height);

    // Apply min/max constraints
    height = Math.max(this.minHeight, Math.min(height, this.maxHeight));

    return height;
  }

  /**
   * Notify parent of height change
   */
  private notifyHeightChange(height: number): void {
    // Only notify if height has changed significantly (>5px difference)
    if (Math.abs(height - this.lastHeight) < 5) {
      return;
    }

    this.lastHeight = height;

    this.log('Height changed', { height });

    // Call callback if provided
    if (this.onResize) {
      this.onResize(height);
    }

    // Send resize message to parent
    try {
      const messagingService = getMessagingService();
      messagingService.send(MessageType.RESIZE_REQUEST, {
        data: {
          height,
          width: undefined, // Let parent control width
        },
      });
    } catch (error) {
      // MessagingService might not be initialized yet
      this.log('MessagingService not available', { error });
    }
  }

  /**
   * Manually trigger resize check
   */
  public checkResize(): void {
    const height = this.getCurrentHeight();
    this.notifyHeightChange(height);
  }

  /**
   * Update configuration
   */
  public updateConfig(config: Partial<ResizeConfig>): void {
    if (config.debounceDelay !== undefined) {
      this.debounceDelay = config.debounceDelay;
    }
    if (config.minHeight !== undefined) {
      this.minHeight = config.minHeight;
    }
    if (config.maxHeight !== undefined) {
      this.maxHeight = config.maxHeight;
    }
    if (config.debug !== undefined) {
      this.debug = config.debug;
    }
    if (config.onResize !== undefined) {
      this.onResize = config.onResize;
    }

    this.log('Configuration updated', config);
  }

  /**
   * Debug logging
   */
  private log(message: string, data?: unknown): void {
    if (this.debug) {
      console.log(`[ResizeService] ${message}`, data || '');
    }
  }

  /**
   * Cleanup and destroy service
   */
  public destroy(): void {
    this.stop();
    this.log('Service destroyed');
  }
}

/**
 * Singleton resize service instance
 */
let resizeServiceInstance: ResizeService | null = null;

/**
 * Initialize resize service
 */
export function initResizeService(config?: ResizeConfig): ResizeService {
  if (resizeServiceInstance) {
    resizeServiceInstance.destroy();
  }

  resizeServiceInstance = new ResizeService(config);
  resizeServiceInstance.start();

  return resizeServiceInstance;
}

/**
 * Get resize service instance
 */
export function getResizeService(): ResizeService {
  if (!resizeServiceInstance) {
    throw new Error('ResizeService not initialized. Call initResizeService first.');
  }
  return resizeServiceInstance;
}

/**
 * Destroy resize service
 */
export function destroyResizeService(): void {
  if (resizeServiceInstance) {
    resizeServiceInstance.destroy();
    resizeServiceInstance = null;
  }
}
